import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from '@/hooks/ui/use-toast';

// Initialize Stripe with proper error handling
// Note: Stripe.js shows HTTPS warning in development - this is normal for localhost
const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : Promise.resolve(null);

export interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  currency: string;
  max_businesses: number | null;
  max_employees_per_business: number | null;
  max_total_employees: number | null;
  max_shifts_per_month: number | null;
  max_loyalty_programs: number | null;
  max_storage_mb: number | null;
  max_monthly_emails: number | null;
  features: Record<string, boolean>;
  trial_days: number;
  is_active: boolean;
  is_featured: boolean;
  sort_order: number;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  stripe_product_id: string | null;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'incomplete_expired';
  billing_cycle: 'monthly' | 'yearly';
  amount: number;
  currency: string;
  trial_start: string | null;
  trial_end: string | null;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  ended_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_latest_invoice_id: string | null;
  stripe_default_payment_method: string | null;
  last_usage_reset: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface PaymentMethod {
  id: string;
  user_id: string;
  stripe_payment_method_id: string;
  type: 'card' | 'google_pay' | 'apple_pay' | 'sepa_debit' | 'link' | 'us_bank_account';
  brand: string | null;
  last_four: string | null;
  exp_month: number | null;
  exp_year: number | null;
  country: string | null;
  is_default: boolean;
  is_active: boolean;
  billing_details: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionUsage {
  businesses_count: number;
  employees_count: number;
  shifts_count: number;
  loyalty_programs_count: number;
  storage_used_mb: number;
  monthly_emails_sent: number;
  api_calls_count: number;
  period_start: string;
  period_end: string;
}

export interface SubscriptionSummary {
  plan_name: string;
  plan_display_name: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  businesses_used: number;
  businesses_limit: number | null;
  employees_used: number;
  employees_limit: number | null;
  shifts_used: number;
  shifts_limit: number | null;
  storage_used_mb: number;
  storage_limit_mb: number | null;
  features_available: Record<string, boolean>;
}

interface SubscriptionContextType {
  // Data
  subscription: UserSubscription | null;
  currentPlan: SubscriptionPlan | null;
  availablePlans: SubscriptionPlan[];
  paymentMethods: PaymentMethod[];
  subscriptionSummary: SubscriptionSummary | null;
  usage: SubscriptionUsage | null;

  // State
  isLoading: boolean;
  error: string | null;
  isStripeAvailable: boolean;
  isPaymentsEnabled: boolean;

  // Actions
  checkLimit: (feature: string, businessId?: string, increment?: number) => Promise<boolean>;
  checkFeature: (featureName: string) => boolean;
  createSubscription: (planId: string, billingCycle?: 'monthly' | 'yearly') => Promise<string>;
  cancelSubscription: () => Promise<void>;
  updateSubscription: (planId: string, billingCycle?: 'monthly' | 'yearly') => Promise<void>;
  addPaymentMethod: (paymentMethodId: string, makeDefault?: boolean) => Promise<void>;
  removePaymentMethod: (paymentMethodId: string) => Promise<void>;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
  createSetupIntent: () => Promise<{ client_secret: string; setup_intent_id: string }>;
  refreshData: () => Promise<void>;
  
  // Stripe
  stripe: any;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [stripe, setStripe] = useState<any>(null);
  const [isStripeAvailable, setIsStripeAvailable] = useState<boolean>(false);

  // Subscription system is always enabled
  const isPaymentsEnabled = true;

  // Direct state management for subscription data
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [subscriptionSummary, setSubscriptionSummary] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);

  // Loading states
  const [plansLoading, setPlansLoading] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Initialize Stripe
  useEffect(() => {
    const initStripe = async () => {
      try {
        const stripeInstance = await stripePromise;
        setStripe(stripeInstance);
        setIsStripeAvailable(!!stripeInstance);
        
        if (!stripeInstance) {
          console.warn('Stripe not initialized: VITE_STRIPE_PUBLISHABLE_KEY not configured');
        }
      } catch (error) {
        console.error('Failed to initialize Stripe:', error);
        setIsStripeAvailable(false);
        setStripe(null);
      }
    };
    initStripe();
  }, []);

  // Fetch subscription plans with direct state management
  const fetchSubscriptionPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      setAvailablePlans(data as SubscriptionPlan[]);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      toast.error("Error loading subscription plans", {
        description: error instanceof Error ? error.message : String(error),
      });
      setAvailablePlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, [fetchSubscriptionPlans]);

  // Fetch user subscription with direct state management
  const fetchUserSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setSubscriptionLoading(false);
      return;
    }

    setSubscriptionLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          setSubscription(null); // No subscription found
        } else {
          throw error;
        }
      } else {
        setSubscription(data as UserSubscription);
      }
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      toast.error("Error loading subscription", {
        description: error instanceof Error ? error.message : String(error),
      });
      setSubscription(null);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserSubscription();
  }, [fetchUserSubscription]);

  // Fetch current plan details with direct state management
  const fetchCurrentPlan = useCallback(async () => {
    if (!subscription?.plan_id) {
      setCurrentPlan(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', subscription.plan_id)
        .single();

      if (error) throw error;
      setCurrentPlan(data as SubscriptionPlan);
    } catch (error) {
      console.error('Error fetching current plan:', error);
      setCurrentPlan(null);
    }
  }, [subscription?.plan_id]);

  useEffect(() => {
    fetchCurrentPlan();
  }, [fetchCurrentPlan]);

  // Fetch payment methods with direct state management
  const fetchPaymentMethods = useCallback(async () => {
    if (!user) {
      setPaymentMethods([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPaymentMethods(data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
    }
  }, [user]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  // Fetch subscription summary with usage - direct state management
  const fetchSubscriptionSummary = useCallback(async () => {
    if (!user) {
      setSubscriptionSummary(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('get_subscription_summary', { p_user_id: user.id });

      if (error) {
        if (error.code === 'PGRST116') {
          setSubscriptionSummary(null); // No subscription found
        } else {
          throw error;
        }
      } else {
        setSubscriptionSummary(data[0] || null);
      }
    } catch (error) {
      console.error('Error fetching subscription summary:', error);
      setSubscriptionSummary(null);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscriptionSummary();
  }, [fetchSubscriptionSummary]);

  // Fetch current usage with direct state management
  const fetchUsage = useCallback(async () => {
    if (!subscription) {
      setUsage(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('get_subscription_usage', {
          p_subscription_id: subscription.id
        });

      if (error) throw error;
      setUsage(data[0] || null);
    } catch (error) {
      console.error('Error fetching subscription usage:', error);
      setUsage(null);
    }
  }, [subscription?.id]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const isLoading = plansLoading || subscriptionLoading;

  // Helper function to get session token
  const getAuthToken = async (): Promise<string> => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) throw new Error('Not authenticated');
    return session.access_token;
  };

  // Check subscription limit
  const checkLimit = async (
    feature: string,
    businessId?: string,
    increment: number = 1
  ): Promise<boolean> => {
    if (!user) return false;

    // If payments are disabled globally, allow all actions
    if (!isPaymentsEnabled) {
      console.warn('Subscription limits disabled: Payments system is turned off');
      return true;
    }

    // If Stripe is not available, allow all actions (graceful degradation)
    if (!isStripeAvailable) {
      console.warn('Subscription limits disabled: Stripe not configured');
      return true;
    }

    try {
      const { data, error } = await supabase
        .rpc('check_subscription_limit', {
          p_user_id: user.id,
          p_business_id: businessId || null,
          p_feature: feature,
          p_increment: increment,
        });
      
      if (error) {
        console.error('Error checking subscription limit:', error);
        return false;
      }
      
      return data;
    } catch (error) {
      console.error('Error checking subscription limit:', error);
      return false;
    }
  };

  // Check feature availability
  const checkFeature = (featureName: string): boolean => {
    // If payments are disabled globally, allow all features
    if (!isPaymentsEnabled) {
      console.warn('Feature checks disabled: Payments system is turned off');
      return true;
    }

    // If Stripe is not available, allow all features (graceful degradation)
    if (!isStripeAvailable) {
      console.warn('Feature checks disabled: Stripe not configured');
      return true;
    }

    if (!currentPlan) return false;
    return currentPlan.features[featureName] === true;
  };

  // Create subscription
  const createSubscription = async (
    planId: string, 
    billingCycle: 'monthly' | 'yearly' = 'monthly'
  ): Promise<string> => {
    if (!isStripeAvailable) {
      throw new Error('Subscription creation unavailable: Stripe not configured');
    }
    
    try {
      setError(null);
      const token = await getAuthToken();
      
      const response = await fetch('/functions/v1/create-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabase.supabaseKey,
        },
        body: JSON.stringify({
          plan_id: planId,
          billing_cycle: billingCycle,
          success_url: `${window.location.origin}/create-business?subscription=success`,
          cancel_url: `${window.location.origin}/select-plan?subscription=canceled`,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create subscription');
      }

      // Refresh subscription data after creation
      await refreshData();
      
      return data.checkout_url || data.subscription_id;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create subscription';
      setError(errorMessage);
      throw error;
    }
  };

  // Cancel subscription
  const cancelSubscription = async (): Promise<void> => {
    if (!isStripeAvailable) {
      throw new Error('Subscription management unavailable: Stripe not configured');
    }
    
    if (!subscription?.stripe_subscription_id) {
      throw new Error('No active subscription to cancel');
    }

    try {
      setError(null);
      const token = await getAuthToken();
      
      const response = await fetch('/functions/v1/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabase.supabaseKey,
        },
        body: JSON.stringify({
          subscription_id: subscription.stripe_subscription_id,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      // Refresh subscription data
      await refreshData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel subscription';
      setError(errorMessage);
      throw error;
    }
  };

  // Update subscription
  const updateSubscription = async (
    planId: string, 
    billingCycle: 'monthly' | 'yearly' = 'monthly'
  ): Promise<void> => {
    if (!isStripeAvailable) {
      throw new Error('Subscription management unavailable: Stripe not configured');
    }
    
    try {
      setError(null);
      const token = await getAuthToken();
      
      const response = await fetch('/functions/v1/update-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabase.supabaseKey,
        },
        body: JSON.stringify({
          plan_id: planId,
          billing_cycle: billingCycle,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update subscription');
      }

      // Refresh subscription data
      await refreshData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update subscription';
      setError(errorMessage);
      throw error;
    }
  };

  // Create setup intent for adding payment methods
  const createSetupIntent = async (): Promise<{ client_secret: string; setup_intent_id: string }> => {
    if (!isStripeAvailable) {
      throw new Error('Payment method management unavailable: Stripe not configured');
    }
    
    try {
      setError(null);
      const token = await getAuthToken();
      
      const response = await fetch('/functions/v1/manage-payment-methods', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabase.supabaseKey,
        },
        body: JSON.stringify({
          action: 'create_setup_intent',
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create setup intent');
      }

      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create setup intent';
      setError(errorMessage);
      throw error;
    }
  };

  // Add payment method
  const addPaymentMethod = async (paymentMethodId: string, makeDefault: boolean = false): Promise<void> => {
    if (!isStripeAvailable) {
      throw new Error('Payment method management unavailable: Stripe not configured');
    }
    
    try {
      setError(null);
      const token = await getAuthToken();
      
      const response = await fetch('/functions/v1/manage-payment-methods', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabase.supabaseKey,
        },
        body: JSON.stringify({
          action: 'attach',
          payment_method_id: paymentMethodId,
          make_default: makeDefault,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add payment method');
      }

      // Refresh payment methods
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add payment method';
      setError(errorMessage);
      throw error;
    }
  };

  // Remove payment method
  const removePaymentMethod = async (paymentMethodId: string): Promise<void> => {
    if (!isStripeAvailable) {
      throw new Error('Payment method management unavailable: Stripe not configured');
    }
    
    try {
      setError(null);
      const token = await getAuthToken();
      
      const response = await fetch('/functions/v1/manage-payment-methods', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabase.supabaseKey,
        },
        body: JSON.stringify({
          action: 'detach',
          payment_method_id: paymentMethodId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove payment method');
      }

      // Refresh payment methods
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove payment method';
      setError(errorMessage);
      throw error;
    }
  };

  // Set default payment method
  const setDefaultPaymentMethod = async (paymentMethodId: string): Promise<void> => {
    if (!isStripeAvailable) {
      throw new Error('Payment method management unavailable: Stripe not configured');
    }
    
    try {
      setError(null);
      const token = await getAuthToken();
      
      const response = await fetch('/functions/v1/manage-payment-methods', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabase.supabaseKey,
        },
        body: JSON.stringify({
          action: 'set_default',
          payment_method_id: paymentMethodId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to set default payment method');
      }

      // Refresh payment methods
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to set default payment method';
      setError(errorMessage);
      throw error;
    }
  };

  // Refresh all subscription data with direct function calls
  const refreshData = async (): Promise<void> => {
    await Promise.all([
      fetchUserSubscription(),
      fetchCurrentPlan(),
      fetchPaymentMethods(),
      fetchSubscriptionSummary(),
      fetchUsage(),
      fetchSubscriptionPlans()
    ]);
  };

  const value: SubscriptionContextType = {
    // Data
    subscription,
    currentPlan,
    availablePlans,
    paymentMethods,
    subscriptionSummary,
    usage,

    // State
    isLoading,
    error,
    isStripeAvailable,
    isPaymentsEnabled,

    // Actions
    checkLimit,
    checkFeature,
    createSubscription,
    cancelSubscription,
    updateSubscription,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
    createSetupIntent,
    refreshData,
    
    // Stripe
    stripe,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};